import * as Sentry from "@sentry/react";
import { InfiniteData, QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Auth from "src/Auth";
import { CHAT_RECENT_TURN_LIMIT, chatQueryKey, DefaultChatModel, NEW_CHAT_ID } from "./PersistentChatTypes";
import { PersistentChatMessageData, PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import { sendAppEvent } from "src/Utils";
import { CHAT_LIST_PAGE_SIZE } from "../../../../src/shared/persistentChatBranching";

function messageTimeMs(row: { created_at?: Date | string }): number {
  const v = row.created_at;
  if (!v) return 0;
  const t = v instanceof Date ? v.getTime() : new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

export async function fetchChatPage(
  chatId: string,
  options?: { beforeMessageId?: string | null },
): Promise<PersistentChatWithMessages> {
  const response = await Auth.fetchWithIdToken("/api/chats/get", {
    chatId,
    recentTurnLimit: CHAT_RECENT_TURN_LIMIT,
    ...(options?.beforeMessageId ? { beforeMessageId: options.beforeMessageId } : {}),
  });

  if (!response?.success) throw new Error("Failed to fetch chat");

  return response.chat as PersistentChatWithMessages;
}

export async function fetchFullChatForCopy(chatId: string): Promise<PersistentChatWithMessages> {
  const response = await Auth.fetchWithIdToken("/api/chats/get", { chatId });

  if (!response?.success) throw new Error("Failed to fetch chat");

  return response.chat as PersistentChatWithMessages;
}

export function mergeOlderChatMessages(
  previous: PersistentChatWithMessages | undefined,
  olderPage: PersistentChatWithMessages,
): PersistentChatWithMessages {
  if (!previous) return olderPage;

  const existingIds = new Set((previous.messages ?? []).map((m) => m.id));
  const prepended = (olderPage.messages ?? []).filter((m) => !existingIds.has(m.id));
  const mergedMessages = [...prepended, ...(previous.messages ?? [])].sort(
    (a, b) => messageTimeMs(a) - messageTimeMs(b),
  );

  const oldestLoaded = mergedMessages[0];
  const olderPagination = olderPage.messagePagination;
  const previousPagination = previous.messagePagination;
  const gotNewOlderRows = prepended.length > 0;
  const mergedHasMoreOlder = gotNewOlderRows
    ? (olderPagination?.hasMoreOlder ?? previousPagination?.hasMoreOlder ?? false)
    : (previousPagination?.hasMoreOlder ?? olderPagination?.hasMoreOlder ?? false);
  return {
    ...previous,
    messages: mergedMessages,
    messagePagination: olderPagination ?? previousPagination
      ? {
          ...(previousPagination ?? {}),
          ...(olderPagination ?? {}),
          hasMoreOlder: mergedHasMoreOlder,
          oldestLoadedMessageId: oldestLoaded?.id ?? olderPagination?.oldestLoadedMessageId ?? previousPagination?.oldestLoadedMessageId ?? null,
        }
      : null,
  };
}

/** Keep scroll-loaded prefix when a refetch only returns the recent tail page. */
export function mergeRefetchedChatTail(
  previous: PersistentChatWithMessages | undefined,
  refetched: PersistentChatWithMessages,
): PersistentChatWithMessages {
  if (!previous?.messages?.length) return refetched;

  const refetchedIds = new Set((refetched.messages ?? []).map((m) => m.id));
  const olderPrefix = previous.messages.filter((m) => !refetchedIds.has(m.id));
  if (olderPrefix.length === 0) return refetched;

  const mergedMessages = [...olderPrefix, ...(refetched.messages ?? [])].sort(
    (a, b) => messageTimeMs(a) - messageTimeMs(b),
  );

  const refetchedPagination = refetched.messagePagination;
  const previousPagination = previous.messagePagination;

  return {
    ...refetched,
    messages: mergedMessages,
    messagePagination: {
      // Prefer the fresh tail refetch so stale client "has more" does not stick after history is fully loaded.
      hasMoreOlder: refetchedPagination?.hasMoreOlder ?? previousPagination?.hasMoreOlder ?? false,
      oldestLoadedMessageId:
        olderPrefix[0]?.id ?? refetchedPagination?.oldestLoadedMessageId ?? previousPagination?.oldestLoadedMessageId ?? null,
      turnPageSize: refetchedPagination?.turnPageSize ?? previousPagination?.turnPageSize ?? CHAT_RECENT_TURN_LIMIT,
    },
  };
}

export const useCurrentChat = (projectId: string, chatId: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: chatQueryKey(projectId, chatId),
    queryFn: async () => {
      if (chatId === NEW_CHAT_ID) return DefaultChatModel;

      const page = await fetchChatPage(chatId);
      const key = chatQueryKey(projectId, chatId);
      const previous = queryClient.getQueryData<PersistentChatWithMessages>(key);
      return mergeRefetchedChatTail(previous, page);
    },
    refetchOnWindowFocus: chatId !== NEW_CHAT_ID,
    refetchOnMount: chatId !== NEW_CHAT_ID,
    staleTime: chatId === NEW_CHAT_ID ? Infinity : undefined,

    onError: (error) => {
      Sentry.captureException(error);
    },
  });
};

export const useSendMessage = () =>
  useMutation({
    mutationFn: async ({ chatId, role, content, metadata }: { chatId: string; role: "user" | "assistant" | "system"; content: string; metadata?: any }) => {
      const response = await Auth.fetchWithIdToken("/api/chats/add-message", {
        chatId,
        role,
        content,
        metadata,
      });

      if (!response?.success) throw new Error("Failed to send message");

      return response.message as PersistentChatMessageData;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },

    onSuccess: () => {
      sendAppEvent("Chat Message Sent");
    },
  });

export const useProjectChats = (projectId: string, seriesId?: string | null) =>
  useProjectChatsList(projectId, "", seriesId);

type ProjectChatsPage = {
  chats: PersistentChatWithMessages[];
  hasMore: boolean;
  nextOffset: number;
};

export const projectChatsListQueryKey = (projectId: string, searchTerm = "", seriesId?: string | null) =>
  ["chats", projectId, searchTerm.trim(), seriesId ?? null] as const;

export function snapshotProjectChatsListCaches(queryClient: QueryClient, projectId: string) {
  return queryClient.getQueriesData<InfiniteData<ProjectChatsPage>>({ queryKey: ["chats", projectId] });
}

export function restoreProjectChatsListCaches(
  queryClient: QueryClient,
  snapshots: ReturnType<typeof snapshotProjectChatsListCaches>,
) {
  for (const [key, data] of snapshots) {
    queryClient.setQueryData(key, data);
  }
}

export function updateProjectChatsListCaches(
  queryClient: QueryClient,
  projectId: string,
  updater: (chats: PersistentChatWithMessages[]) => PersistentChatWithMessages[],
) {
  queryClient.setQueriesData<InfiniteData<ProjectChatsPage>>(
    { queryKey: ["chats", projectId] },
    (old) => {
      if (!old) return old;
      if (!Array.isArray(old.pages)) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          chats: updater(page.chats),
        })),
      };
    },
  );
}

export const useProjectChatsList = (projectId: string, searchTerm = "", seriesId?: string | null) =>
  useInfiniteQuery({
    queryKey: projectChatsListQueryKey(projectId, searchTerm, seriesId),
    enabled: Boolean(projectId),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await Auth.fetchWithIdToken("/api/chats/get-by-project", {
        projectId,
        limit: CHAT_LIST_PAGE_SIZE,
        offset: pageParam,
        searchTerm: searchTerm.trim() || undefined,
        ...(seriesId ? { seriesId } : {}),
      });

      if (!response?.success) throw new Error("Failed to fetch chats");

      const chats = ((response.chats || []) as PersistentChatWithMessages[]).map((chat) => ({
        ...chat,
        messages: chat.messages ?? [],
      }));
      const pagination = response.pagination || {};
      return {
        chats,
        hasMore: Boolean(pagination.hasMore),
        nextOffset: Number.isFinite(pagination.nextOffset) ? pagination.nextOffset : pageParam + chats.length,
      } as ProjectChatsPage;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextOffset : undefined),
    select: (data) => ({
      ...data,
      chats: data.pages.flatMap((page) => page.chats),
    }),
    onError: (error) => {
      Sentry.captureException(error);
    },
    refetchOnWindowFocus: true,
    retry: 3,
  });

export const useCreateChat = () => {
  return useMutation({
    mutationFn: async ({ title, projectId, initialMessage }: { projectId: string; title: string; initialMessage?: string }) => {
      const response = await Auth.fetchWithIdToken("/api/chats/create", {
        projectId,
        title,
        initialMessage,
      });

      if (!response?.success) throw new Error("Failed to create chat");

      return response.chat as PersistentChatWithMessages;
    },

    onError: (error) => {
      Sentry.captureException(error);
    },

    onSuccess: () => {
      sendAppEvent("Chat Created");
    },
  });
};

export const useUpdateChat = () =>
  useMutation({
    mutationFn: async ({ chatId, title }: { chatId: string; title: string }) => {
      const response = await Auth.fetchWithIdToken(`/api/chats/${chatId}`, {
        title,
        method: "PUT",
      });

      if (!response?.success) throw new Error("Failed to update chat");

      return response.chat as PersistentChatWithMessages;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: () => {
      sendAppEvent("Chat Title Updated");
    },
  });

export const useDeleteChat = () =>
  useMutation({
    mutationFn: async ({ chatId }: { chatId: string }) => {
      const response = await Auth.fetchWithIdToken(`/api/chats/${chatId}`, {
        method: "DELETE",
      });
      if (!response?.success) throw new Error("Failed to delete chat");
      return response;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: () => {
      sendAppEvent("Chat Deleted");
    },
  });

export const useGenerateChatTitle = () =>
  useMutation({
    mutationFn: async ({ messages, projectId, documentId }: { messages: PersistentChatMessageData[]; projectId: string; documentId: string }) => {
      const value: string = await new Promise((resolve, reject) => {
        let title = "";

        Auth.streamWithIdToken(
          "/api/ai/agenticChat/generateTitle",
          {
            history: messages,
            projectId,
            documentId,
          },
          null,
          // onChunk
          (chunk: any) => {
            if (chunk.text) {
              title += chunk.text;
            }
          },
          // onDone
          () => {
            resolve(title?.trim());
          },
          (chunk: any) => {
            reject(chunk);
          },
        );
      });

      if (value) {
        let title = value.trim();

        if (title.startsWith('"')) title = title.slice(1);
        if (title.endsWith('"')) title = title.slice(0, -1);

        return title;
      }

      throw new Error("Failed to generate chat title");
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: () => {
      sendAppEvent("Chat Title Generated");
    },
  });

export const useSetActiveBranch = (projectId: string, onSwitchError?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ chatId, leafMessageId }: { chatId: string; leafMessageId: string }) => {
      const response = await Auth.fetchWithIdToken("/api/chats/set-active-branch", {
        chatId,
        leafMessageId,
        recentTurnLimit: CHAT_RECENT_TURN_LIMIT,
      });
      if (!response?.success) throw new Error("Failed to switch branch");
      return response;
    },
    onMutate: async ({ chatId, leafMessageId }) => {
      const key = chatQueryKey(projectId, chatId);
      await queryClient.cancelQueries({ queryKey: key });

      const previousChat = queryClient.getQueryData<PersistentChatWithMessages>(key);
      if (previousChat) {
        queryClient.setQueryData<PersistentChatWithMessages>(key, {
          ...previousChat,
          active_leaf_message_id: leafMessageId,
        });
      }

      return { previousChat, key };
    },
    onError: (error, _variables, context) => {
      if (context?.previousChat && context.key) {
        queryClient.setQueryData(context.key, context.previousChat);
      }
      Sentry.captureException(error);
      onSwitchError?.();
    },
    onSettled: (_data, _error, { chatId }) => {
      queryClient.invalidateQueries({ queryKey: chatQueryKey(projectId, chatId) });
    },
  });
};
